import dayjs from 'dayjs';
import type {
    AccountingPeriod,
    AccountingPeriodStatus,
    FiscalYear,
} from '@/api/models';

/**
 * Pure rules and presentational config for the Periods screen.
 *
 * Everything the screen refuses, it refuses *here* and says why — the UI never
 * hides an action it could explain instead (the disabled-with-reason pattern the
 * entry form established).
 */

/**
 * Soft status-pill styling, in the journal's calm vocabulary (the same language
 * as Draft / Posted / Reversed). A closed period is ordinary bookkeeping, so it
 * reads neutral-affirmative — never as an error. LOCKED is the most muted of the
 * three: it is the most settled state, not the most alarming one.
 */
export const PERIOD_STATUS_CHIP: Record<
    AccountingPeriodStatus,
    { className: string; labelKey: string }
> = {
    OPEN: {
        className: 'bg-neutral-200 text-neutral-700',
        labelKey: 'periods.status.open',
    },
    CLOSED: {
        className: 'bg-green-100 text-green-800',
        labelKey: 'periods.status.closed',
    },
    LOCKED: {
        className: 'bg-neutral-200 text-neutral-500',
        labelKey: 'periods.status.locked',
    },
};

/** What a row may do, and why not when it may not. */
export type PeriodActions = {
    canClose: boolean;
    canReopen: boolean;
    /**
     * i18n key explaining why neither action is offered, or `null` when at
     * least one is.
     */
    disabledReasonKey: string | null;
    /** Interpolation for the reason (how many neighbouring periods block it). */
    disabledReasonCount: number;
    /**
     * `YYYY-MM` of the period that has to move first, for the reasons that name
     * one, or `null` for the reasons that don't. The view localizes it — this
     * module never resolves a month name.
     */
    disabledReasonPeriod: string | null;
};

/**
 * What the row's neighbours and its year make possible, beyond its own status.
 *
 * Defaulted so a caller that only knows the status still gets the plain
 * status→action mapping.
 */
export type PeriodActionContext = {
    /** Periods before this one that are still OPEN — the server's close order. */
    earlierOpenCount?: number;
    /** Periods after this one that are still CLOSED — the server's reopen order. */
    laterClosed?: LaterClosedBlock;
    /** The containing year is CLOSED or LOCKED, so its months are settled. */
    isYearClosed?: boolean;
};

/**
 * Status → available actions. The single mapping the row renderer and the tests
 * both read.
 *
 * - OPEN   → Close, **if every earlier period is already closed**
 * - CLOSED → Reopen, **if no later period is still closed**
 * - LOCKED → neither, because the period is sealed behind the fiscal year's
 *            closing entry; reopening the year is the only way back.
 *
 * ## The three rules that come from the server, not from taste
 *
 * 1. **Periods close in order.** `POST …/periods/6/close` with 2–5 still open is
 *    refused: *"Close earlier period(s) first: 2, 3, 4, 5."* Measured live. The
 *    gate is applied here so the row explains itself instead of offering a
 *    button that fails.
 * 2. **Periods reopen in the opposite order — newest first.** This is new: the
 *    API used to reopen in any order, and now refuses with the exact mirror of
 *    the close message, *"Reopen later period(s) first: 2, 3, 4."* Re-measured
 *    2026-07-31 against the dev tenant with January–May closed: reopening 1 was
 *    refused naming 2, 3, 4, 5; reopening 3 was refused naming 4, 5; reopening 5
 *    succeeded, after which 4 became the reopenable one. Because closes are a
 *    strict prefix, exactly one period is reopenable at a time — the newest
 *    closed one — so the reason can name it rather than count.
 *
 *    **The refusal lists later *`CLOSED`* periods only** — later OPEN periods are
 *    excluded from the list, verified directly. What is *not* measured is whether
 *    a later `LOCKED` period also blocks: producing one costs a permanent
 *    `/lock` with no inverse anywhere in the API, so the tenant was not spent on
 *    it. This gate therefore counts **`CLOSED` only**, deliberately, because the
 *    two ways to be wrong are not symmetric: counting `LOCKED` too would refuse
 *    a reopen the server may well allow *and* tell the user to reopen a period
 *    that can never be reopened, permanently and falsely. Counting only `CLOSED`
 *    risks at worst one refusal that reaches the click — which is already
 *    handled, and shown in the server's own words.
 * 3. **A closed year seals its months even though their status stays CLOSED.**
 *    The server does not relabel periods `LOCKED` on a year close — `LOCKED` is
 *    reachable only through the `/lock` paths this client never calls. Reopening
 *    a month underneath a closed year would leave the year's CLOSING entry
 *    covering a month that is open again, so the screen refuses it and says why.
 *    This is **stricter than the API was measured to be** (the server's own
 *    behaviour here was not exercised, because doing so costs a permanent
 *    closing entry and its reversal on the tenant); being stricter can only turn
 *    a confusing success or an opaque prose refusal into a stated reason.
 */
export const getPeriodActions = (
    status: AccountingPeriodStatus,
    context: PeriodActionContext = {},
): PeriodActions => {
    const { earlierOpenCount = 0, laterClosed, isYearClosed = false } = context;

    if (isYearClosed) {
        return {
            canClose: false,
            canReopen: false,
            disabledReasonKey: 'periods.reasons.yearClosedSealsPeriods',
            disabledReasonCount: 0,
            disabledReasonPeriod: null,
        };
    }

    switch (status) {
        case 'OPEN':
            return earlierOpenCount > 0
                ? {
                      canClose: false,
                      canReopen: false,
                      disabledReasonKey: 'periods.reasons.earlierPeriodsOpen',
                      disabledReasonCount: earlierOpenCount,
                      disabledReasonPeriod: null,
                  }
                : {
                      canClose: true,
                      canReopen: false,
                      disabledReasonKey: null,
                      disabledReasonCount: 0,
                      disabledReasonPeriod: null,
                  };
        case 'CLOSED':
            return laterClosed && laterClosed.count > 0
                ? {
                      canClose: false,
                      canReopen: false,
                      disabledReasonKey: 'periods.reasons.laterPeriodsClosed',
                      disabledReasonCount: laterClosed.count,
                      disabledReasonPeriod: laterClosed.nextYearMonth,
                  }
                : {
                      canClose: false,
                      canReopen: true,
                      disabledReasonKey: null,
                      disabledReasonCount: 0,
                      disabledReasonPeriod: null,
                  };
        case 'LOCKED':
            return {
                canClose: false,
                canReopen: false,
                disabledReasonKey: 'periods.reasons.periodLocked',
                disabledReasonCount: 0,
                disabledReasonPeriod: null,
            };
    }
};

/**
 * How many periods **before** each one are still open, keyed by period id.
 *
 * Computed once for the year rather than per row, so the table stays O(n) and
 * every row reads the same answer.
 */
export const getEarlierOpenCounts = (
    periods: readonly AccountingPeriod[],
): Map<string, number> => {
    const counts = new Map<string, number>();
    let running = 0;
    for (const period of [...periods].sort((a, b) => a.sequence - b.sequence)) {
        counts.set(period.id, running);
        if (period.status === 'OPEN') {
            running += 1;
        }
    }
    return counts;
};

/** What stands between a closed period and its Reopen, if anything. */
export type LaterClosedBlock = {
    /** Later periods still CLOSED. `0` means this row is the reopenable one. */
    count: number;
    /**
     * `YYYY-MM` of the one to reopen next — the **newest** period still closed,
     * because reopening runs newest-first. Empty when {@link count} is `0`.
     */
    nextYearMonth: string;
};

/**
 * How many periods **after** each one are still closed, keyed by period id, and
 * which one has to be reopened next.
 *
 * The exact mirror of {@link getEarlierOpenCounts}: that one walks forwards
 * accumulating still-OPEN periods for the close order, this one walks backwards
 * accumulating still-CLOSED periods for the reopen order. Computed once for the
 * year, so the table stays O(n) and every row reads the same answer.
 *
 * `LOCKED` periods are **not** counted — see {@link getPeriodActions} rule 2 for
 * why that is a decision rather than an oversight.
 */
export const getLaterClosedCounts = (
    periods: readonly AccountingPeriod[],
): Map<string, LaterClosedBlock> => {
    const blocks = new Map<string, LaterClosedBlock>();
    let count = 0;
    // The next one to reopen is the newest still-closed period seen so far —
    // walking backwards, that is simply the first one this loop meets.
    let nextYearMonth = '';
    for (const period of [...periods].sort((a, b) => b.sequence - a.sequence)) {
        blocks.set(period.id, { count, nextYearMonth });
        if (period.status === 'CLOSED') {
            count += 1;
            if (!nextYearMonth) {
                nextYearMonth = period.yearMonth;
            }
        }
    }
    return blocks;
};

/** Whether the year-level close is offered, and why not when it isn't. */
export type YearCloseAvailability = {
    canClose: boolean;
    /** i18n key for the stated reason; `null` when the action is available. */
    reasonKey: string | null;
    /** Interpolation for the reason (open-period count). */
    reasonCount: number;
};

/**
 * "Close year" is available only once **every** period in the year is closed —
 * a year cannot be closed over an open month, because the closing entry would
 * miss postings still to come.
 */
export const getYearCloseAvailability = (
    fiscalYear: FiscalYear | null,
    periods: AccountingPeriod[],
): YearCloseAvailability => {
    if (!fiscalYear) {
        return { canClose: false, reasonKey: null, reasonCount: 0 };
    }
    // Anything but OPEN is already past closing — LOCKED included, and there is
    // no way back from that one through the API at all.
    if (fiscalYear.status !== 'OPEN') {
        return {
            canClose: false,
            reasonKey: 'periods.reasons.yearAlreadyClosed',
            reasonCount: 0,
        };
    }
    if (periods.length === 0) {
        return {
            canClose: false,
            reasonKey: 'periods.reasons.noPeriods',
            reasonCount: 0,
        };
    }
    const openCount = periods.filter(
        (period) => period.status === 'OPEN',
    ).length;
    if (openCount > 0) {
        return {
            canClose: false,
            reasonKey: 'periods.reasons.periodsStillOpen',
            reasonCount: openCount,
        };
    }
    return { canClose: true, reasonKey: null, reasonCount: 0 };
};

/** Whether the retained-earnings rollover is offered, and why not when it isn't. */
export const getRolloverAvailability = (
    fiscalYear: FiscalYear | null,
): YearCloseAvailability => {
    if (!fiscalYear) {
        return { canClose: false, reasonKey: null, reasonCount: 0 };
    }
    if (fiscalYear.status !== 'CLOSED') {
        return {
            canClose: false,
            reasonKey: 'periods.reasons.rolloverNeedsClose',
            reasonCount: 0,
        };
    }
    if (fiscalYear.rolloverEntryId) {
        return {
            canClose: false,
            reasonKey: 'periods.reasons.rolloverDone',
            reasonCount: 0,
        };
    }
    return { canClose: true, reasonKey: null, reasonCount: 0 };
};

/** `DD/MM/YYYY` display for an ISO date, matching the journal. */
export const formatPeriodDate = (iso: string): string =>
    iso ? dayjs(iso, 'YYYY-MM-DD').format('DD/MM/YYYY') : '';

/**
 * i18n key for the month name of a `YYYY-MM`.
 *
 * Month names come from the app's own locale files rather than `Intl` or dayjs.
 * dayjs is loaded without locale packs, so `.locale('ka')` silently yields
 * English; and `Intl` depends on the runtime shipping Georgian ICU data, which
 * not every browser build does — one verified in testing resolved `ka` to
 * `en-US` and rendered "January". Twelve strings in `ka.json` are cheap and
 * always correct.
 */
export const monthLabelKey = (yearMonth: string): string =>
    `periods.months.${Number(yearMonth.slice(5, 7))}`;

/** `<Month> <Year>` from an already-localized month name. */
export const formatPeriodLabel = (
    yearMonth: string,
    monthName: string,
): string => `${monthName} ${yearMonth.slice(0, 4)}`;

/** The inclusive range a close covers, e.g. `01/06/2026 – 30/06/2026`. */
export const formatPeriodRange = (period: {
    startDate: string;
    endDate: string;
}): string =>
    `${formatPeriodDate(period.startDate)} – ${formatPeriodDate(period.endDate)}`;

/**
 * Refusal code → i18n key, so a write that loses a race is reported in the same
 * words as the pre-emptive disabled reason.
 *
 * ## This table is *still* unreachable, re-measured 2026-07-31
 *
 * **The fiscal-year endpoints send no machine-readable code — including the new
 * reopen-ordering refusal.** Every refusal is `{ error: "<English prose>" }` —
 * no `code` (which the journal and tax-settings endpoints do populate) and no
 * `errors` map (which this client's original contract assumed). Re-swept across
 * every refusal these paths produce: an out-of-order close, an **out-of-order
 * reopen**, closing an already-closed period, reopening an already-open one, an
 * unknown period number, an unknown fiscal year, a year close over open periods,
 * a rollover before the close, and a reopen of an open year. Nine refusals, nine
 * bare prose bodies. `REOPEN_OUT_OF_ORDER` does not appear in any response, and
 * the string appears nowhere in `/openapi/v1.json` either.
 *
 * So in practice a refusal that reaches the user is shown in the server's own
 * words, in English. That is acceptable *because it is the second line of
 * defence*: every refusal the screen can anticipate is already stated as a
 * localized disabled reason before the click. The prose path exists for races
 * and for rules this client does not model.
 *
 * The table is kept rather than deleted because the day a `code` lands, binding
 * it is a one-line change and the strings are already translated —
 * `REOPEN_OUT_OF_ORDER` is pre-bound below on exactly that bet.
 */
const ERROR_REASON_KEYS: Record<string, string> = {
    PERIOD_LOCKED: 'periods.reasons.periodLocked',
    REOPEN_OUT_OF_ORDER: 'periods.errors.laterPeriodsClosed',
    PERIOD_ALREADY_CLOSED: 'periods.errors.periodAlreadyClosed',
    PERIOD_ALREADY_OPEN: 'periods.errors.periodAlreadyOpen',
    PERIODS_NOT_CLOSED: 'periods.errors.periodsStillOpen',
    EARLIER_PERIODS_OPEN: 'periods.errors.earlierPeriodsOpen',
    YEAR_ALREADY_CLOSED: 'periods.reasons.yearAlreadyClosed',
    YEAR_NOT_CLOSED: 'periods.reasons.rolloverNeedsClose',
    NOTHING_TO_CLOSE: 'periods.errors.nothingToClose',
    ROLLOVER_ALREADY_POSTED: 'periods.reasons.rolloverDone',
    ENTRY_NOT_REVERSIBLE: 'periods.errors.entryNotReversible',
    JOURNAL_UNAVAILABLE: 'periods.errors.journalUnavailable',
};

/**
 * The i18n key for a failed write, or `null` to fall back to `error.message`.
 *
 * Reads `code` first — the envelope field bos-api actually populates elsewhere —
 * then the legacy `errors.reason`. Today both are absent on this surface and
 * this returns `null` every time; see {@link ERROR_REASON_KEYS}.
 */
export const getErrorReasonKey = (
    error: { code?: string; errors?: Record<string, unknown> } | null,
): string | null => {
    const byCode = error?.code ? ERROR_REASON_KEYS[error.code] : undefined;
    if (byCode) {
        return byCode;
    }
    const reason = error?.errors?.reason;
    return typeof reason === 'string'
        ? (ERROR_REASON_KEYS[reason] ?? null)
        : null;
};

/**
 * The one reason that is about the table rather than about a row (BOS-170).
 *
 * Periods close in order, so on a year with eleven open months every open row
 * except the first carries "Close the earlier periods first — N months before
 * this one are still open." Eleven copies of one rule, differing only in a
 * count, stacked down the page. Repetition at that volume stops being emphasis
 * and becomes noise: the reader scrolls past all eleven, including the one row
 * whose reason is different.
 *
 * The rule is stated once above the table. The rows keep a disabled Close and
 * a short tooltip, which is where a reader looks when they wonder why a
 * particular button will not press.
 */
export const EARLIER_PERIODS_OPEN_REASON = 'periods.reasons.earlierPeriodsOpen';
